import { useEffect, useId, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AlertCircle, CalendarDays, Info, Loader2, Lock, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/cn";
import { useDrawerA11y } from "@/hooks/useDrawerA11y";
import { useClient360Contacts } from "@/hooks/useClients";
import { useScheduleMeeting } from "@/hooks/useClient360Meetings";
import { ContactCreateFlow } from "@/components/clients/ContactCreateFlow";
import { MeetingResponsibleSelect } from "@/components/client360/MeetingResponsibleSelect";
import type { MeetingKind, ScheduleMeetingPayload } from "@/api/client360Meetings";

/**
 * 3C — «Запланувати зустріч» (manual, Google-FREE). Reuses the 3.1A interaction
 * create under the hood; this surface NEVER asks for googleEventId/calendarId/sync
 * and shows an explicit «Джерело: manual» badge. Visit is a gated segment (Этап 5,
 * backend-owned via canScheduleVisit) — never selectable here.
 */
const DURATIONS = [30, 60, 90] as const;

function defaultPlannedAt(): string {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function ScheduleMeetingDrawer({
  open,
  onClose,
  holdingId,
  canScheduleVisit,
  visitDisabledReason,
}: {
  open: boolean;
  onClose: () => void;
  holdingId: string;
  canScheduleVisit?: boolean;
  visitDisabledReason?: string;
}) {
  const panelRef = useDrawerA11y(open, onClose);
  const headingId = useId();
  const queryClient = useQueryClient();
  const mutation = useScheduleMeeting(holdingId);
  const contactsQ = useClient360Contacts(holdingId, {}, open);
  const [assignedUserId, setAssignedUserId] = useState("");

  const [kind, setKind] = useState<MeetingKind>("meeting");
  const [contactId, setContactId] = useState("");
  // S6 «Немає контакту»: живий CTA → in-context ContactCreateFlow (D-034 канон)
  const [contactCreateOpen, setContactCreateOpen] = useState(false);
  const [plannedAt, setPlannedAt] = useState(defaultPlannedAt);
  const [duration, setDuration] = useState<30 | 60 | 90>(60);
  const [goal, setGoal] = useState("");
  const [expected, setExpected] = useState("");
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (open) { setPlannedAt(defaultPlannedAt()); return; }
    setKind("meeting"); setContactId(""); setDuration(60); setGoal(""); setExpected(""); setErr(null); setAssignedUserId("");
  }, [open]);

  const contacts = useMemo(
    () => (contactsQ.data?.items ?? []).filter((c) => !c.isFormer),
    [contactsQ.data],
  );

  if (!open) return null;

  const canSubmit = plannedAt.trim() !== "" && !mutation.isPending;

  const submit = async () => {
    if (plannedAt.trim() === "") { setErr("Вкажіть дату та час зустрічі"); return; }
    const body: ScheduleMeetingPayload = {
      kind,
      plannedAt,
      durationMinutes: duration,
      contactId: contactId || undefined,
      goal: goal.trim() || undefined,
      expectedResult: expected.trim() || undefined,
      assignedUserId: assignedUserId || undefined,
    };
    try {
      await mutation.mutateAsync(body);
      onClose();
    } catch (e) {
      // RACE handling (fixed copy) — keep all draft fields; refresh the picker.
      const status = (e as { status?: number } | null)?.status;
      if (status === 409 || status === 422) {
        setErr("Відповідальний більше не доступний для призначення. Список оновлено.");
        setAssignedUserId("");
        void queryClient.invalidateQueries({ queryKey: ["meetingResponsibleOptions", holdingId] });
      } else if (status === 403) {
        setErr("Клієнт більше не доступний у вашому scope.");
      } else {
        setErr(e instanceof Error ? e.message : "Не вдалося запланувати зустріч");
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose} aria-hidden>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="relative h-full w-full sm:w-[440px] overflow-y-auto bg-background shadow-2xl outline-none"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-background px-5 py-4">
          <h3 id={headingId} className="flex items-center gap-2 text-base font-semibold">
            <CalendarDays className="h-4 w-4 text-primary" aria-hidden /> Запланувати зустріч
          </h3>
          <button type="button" onClick={onClose} aria-label="Закрити" className="rounded-md p-1 text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-5">
          {/* Тип */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Тип</label>
            <div className="flex gap-2">
              <Seg on={kind === "meeting"} onClick={() => setKind("meeting")}>Зустріч</Seg>
              <Seg on={kind === "online"} onClick={() => setKind("online")}>Онлайн</Seg>
              <button
                type="button"
                disabled={!canScheduleVisit}
                title={visitDisabledReason ?? "Доступно після Этапу 5"}
                aria-disabled={!canScheduleVisit}
                className="inline-flex cursor-not-allowed items-center gap-1 rounded-md border border-border px-3 py-1.5 text-sm font-medium text-muted-foreground opacity-50"
              >
                Візит <Lock className="h-3 w-3" aria-hidden />
              </button>
            </div>
            {/* S6: type-aware причина — BACKEND-owned (visitDisabledReason),
                frontend НЕ інферить; fallback = generic Этап-5 текст. */}
            <p className="mt-1.5 text-[11.5px] text-muted-foreground">
              {visitDisabledReason ?? "«Візит» прив'язаний до торгової точки → активний після Этапу 5."}
            </p>
          </div>

          {/* source = manual */}
          <div className="flex items-start gap-2 rounded-md border border-info-foreground/20 bg-info px-3 py-2 text-[11.5px] text-info-foreground">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            <span>Джерело: <b>manual</b> · без Google-синхронізації (Calendar pull — окремий слот 3.1A).</span>
          </div>

          {/* Контакт */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Контакт</label>
            {!contactsQ.isPending && contacts.length === 0 ? (
              /* S6 екран 6 «Немає контакту» — ЖИВИЙ CTA (§0: 0 мертвих
                 placeholder); контакт опціональний (TZ §3 contact opt),
                 але пропонуємо додати одразу. */
              <div className="rounded-md border border-warning-foreground/20 bg-warning px-3 py-2.5 text-xs text-warning-foreground">
                <p>У клієнта ще немає контактів. Зустріч можна запланувати і без контакту.</p>
                <button
                  type="button"
                  onClick={() => setContactCreateOpen(true)}
                  className="mt-2 inline-flex items-center gap-1 rounded-md border border-warning-foreground/40 bg-background px-2.5 py-1.5 text-xs font-semibold text-foreground hover:bg-muted"
                >
                  + Додати контакт
                </button>
              </div>
            ) : (
              <select
                value={contactId}
                onChange={(e) => setContactId(e.target.value)}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">— без контакту —</option>
                {contacts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}{c.position ? ` · ${c.position}` : ""}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Відповідальний (S4 D-068 — own/team-scoped picker; 4 стани) */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Відповідальний</label>
            <MeetingResponsibleSelect
              holdingId={holdingId}
              value={assignedUserId}
              onChange={setAssignedUserId}
              enabled={open}
            />
          </div>

          {/* Дата / час */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
              Дата / час <span className="text-destructive">*</span>
            </label>
            <Input
              type="datetime-local"
              value={plannedAt}
              onChange={(e) => setPlannedAt(e.target.value)}
              className={cn(err && plannedAt.trim() === "" && "border-destructive")}
            />
          </div>

          {/* Тривалість */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Тривалість</label>
            <div className="flex gap-2">
              {DURATIONS.map((d) => (
                <Seg key={d} on={duration === d} onClick={() => setDuration(d)}>{d} хв</Seg>
              ))}
            </div>
          </div>

          {/* Ціль */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Ціль зустрічі</label>
            <Input value={goal} onChange={(e) => setGoal(e.target.value)} placeholder="напр. узгодити асортимент Q3" />
          </div>

          {/* Очікуваний результат */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Очікуваний результат</label>
            <Input value={expected} onChange={(e) => setExpected(e.target.value)} placeholder="напр. замовлення на нову лінійку" />
          </div>

          <div className="flex items-start gap-2 rounded-md border bg-muted/40 px-3 py-2 text-[11.5px] text-muted-foreground">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            <span>Звіт обов'язковий <b>після проведення</b> — без нього зустріч не «завершити» (статус «Проведено», не «Виконано»). Зі звіту створюються задачі/follow-up через Task Core.</span>
          </div>

          {err && (
            <p role="alert" className="flex items-center gap-1.5 text-xs text-destructive">
              <AlertCircle className="h-3.5 w-3.5" aria-hidden /> {err}
            </p>
          )}
        </div>

        <div className="sticky bottom-0 flex gap-2 border-t bg-background px-5 py-4">
          <Button type="button" onClick={submit} disabled={!canSubmit} className="h-11 flex-1 text-sm font-semibold">
            {mutation.isPending ? (<><Loader2 className="h-4 w-4 animate-spin" /> Планування…</>) : "Запланувати"}
          </Button>
          <Button type="button" variant="outline" onClick={onClose} className="h-11 text-sm">Скасувати</Button>
        </div>
      </div>

      {/* S6 — канонічний in-context контакт-флоу (D-034); після закриття
          список контактів перечитується (новий контакт зʼявиться в селекті). */}
      {contactCreateOpen && (
        <ContactCreateFlow
          open
          onClose={() => {
            setContactCreateOpen(false);
            void contactsQ.refetch();
          }}
          holding={{ id: holdingId, name: "" }}
        />
      )}
    </div>
  );
}

function Seg({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center rounded-md border px-3 py-1.5 text-sm font-medium transition-colors",
        on ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-muted",
      )}
    >
      {children}
    </button>
  );
}
